import { useState } from 'react';
import { tiktokVideos, type TikTokVideo } from '@/mocks/tiktok';
import { formatCount } from '@/lib/media';

const CATEGORY_COLORS: Record<TikTokVideo['category'], string> = {
  Tendance: 'bg-accent-500',
  'Nouveauté': 'bg-primary-500',
  Actu: 'bg-secondary-500',
  Humour: 'bg-accent-400',
  Danse: 'bg-primary-400',
  Lifestyle: 'bg-secondary-400',
};

function TikTokFeed() {
  const [active, setActive] = useState<TikTokVideo | null>(null);
  const [filter, setFilter] = useState<'Tous' | TikTokVideo['category']>('Tous');

  const categories: Array<'Tous' | TikTokVideo['category']> = [
    'Tous',
    'Tendance',
    'Nouveauté',
    'Actu',
    'Humour',
    'Danse',
    'Lifestyle',
  ];

  const filtered = filter === 'Tous' ? tiktokVideos : tiktokVideos.filter((v) => v.category === filter);

  return (
    <div>
      {/* Filtres */}
      <div className="flex items-center gap-2 overflow-x-auto no-scrollbar mb-6">
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => setFilter(c)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
              filter === c
                ? 'bg-accent-500 text-foreground-950'
                : 'bg-background-50 text-foreground-400 hover:text-foreground-50 border border-background-300/60'
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Grille verticale */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
        {filtered.map((video) => (
          <button
            key={video.id}
            onClick={() => setActive(video)}
            className="group relative aspect-[9/16] rounded-lg overflow-hidden bg-background-950 text-left"
          >
            <img
              src={video.thumbnail_url}
              alt={video.author}
              title={`${video.author} — ${video.caption}`}
              className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20" />

            {/* Badge catégorie */}
            <span
              className={`absolute top-2 left-2 inline-block px-2 py-0.5 rounded-full text-[10px] font-bold text-foreground-950 ${CATEGORY_COLORS[video.category]}`}
            >
              {video.category}
            </span>

            {/* Bouton play */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-background-950/40 backdrop-blur-sm text-foreground-50 opacity-0 group-hover:opacity-100 transition-opacity">
              <i className="ri-play-fill text-2xl" />
            </div>

            {/* Infos */}
            <div className="absolute inset-x-0 bottom-0 p-3">
              <p className="text-sm font-semibold text-foreground-50 leading-snug line-clamp-2 mb-1.5">
                {video.caption}
              </p>
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-foreground-50 flex items-center gap-1">
                  <i className="ri-user-star-line text-accent-400" />
                  {video.handle}
                </span>
                <span className="flex items-center gap-2 text-[11px] text-foreground-300">
                  <span className="inline-flex items-center gap-0.5">
                    <i className="ri-heart-3-fill text-accent-400" />
                    {formatCount(video.likes)}
                  </span>
                  <span className="inline-flex items-center gap-0.5">
                    <i className="ri-eye-line" />
                    {formatCount(video.views)}
                  </span>
                </span>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Modal lecture */}
      {active && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background-950/90 backdrop-blur-sm"
          onClick={() => setActive(null)}
        >
          <div
            className="relative w-full max-w-sm md:max-w-md rounded-lg overflow-hidden bg-background-950"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setActive(null)}
              className="absolute top-3 right-3 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-background-950/50 backdrop-blur-sm text-foreground-50 hover:bg-background-950/70 transition-colors"
              aria-label="Fermer"
            >
              <i className="ri-close-line text-xl" />
            </button>

            <div className="relative aspect-[9/16] bg-background-950">
              <iframe
                src={`https://www.tiktok.com/embed/v2/${active.video_id}`}
                title={active.caption}
                allowFullScreen
                allow="encrypted-media; picture-in-picture; fullscreen; autoplay"
                className="absolute inset-0 w-full h-full"
              />
            </div>

            <div className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-foreground-950 font-bold text-lg shrink-0">
                  {active.author.charAt(0)}
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-foreground-50 truncate">{active.author}</p>
                  <p className="text-xs text-foreground-500 truncate">
                    {active.handle} · {active.location}
                  </p>
                </div>
              </div>
              <p className="text-sm text-foreground-400 leading-relaxed">{active.caption}</p>
              <div className="flex items-center gap-5 mt-4 text-sm text-foreground-400">
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-heart-3-line text-accent-400" />
                  {formatCount(active.likes)}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-chat-3-line" />
                  {formatCount(active.comments)}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-eye-line" />
                  {formatCount(active.views)} vues
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TikTokFeed;